import { useState } from "react";
import { BRAND } from "../constants/brand";
import PageHero from "../components/PageHero";

const API_URL = process.env.REACT_APP_CHAT_API_URL || "";

const OBJECTIVES = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    title: "Rapid Turnaround Time",
    desc: "To deliver high-quality service, we must be able to provide rapid turnaround time.",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
      </svg>
    ),
    title: "Underserved Communities",
    desc: "To deliver on our social impact mission, we must have locations in underserved communities.",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h.01M15 9h.01M9 15h.01M15 15h.01M12 12h.01"/>
      </svg>
    ),
    title: "AI-Advanced Technology",
    desc: "To deliver on our AI-advanced technology, we must provide analytical services across various laboratory methodologies.",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
      </svg>
    ),
    title: "In-Network Access",
    desc: "To provide access to patients, we are looking for labs with in-network contracts and health system relationships.",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
      </svg>
    ),
    title: "Growth Through Acquisition",
    desc: "To achieve our business goals, we must grow quickly through acquisitions.",
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ),
    title: "Shareholder Value",
    desc: "To deliver shareholder value, we must find opportunities where laboratories are looking to become something more than just 'lab'.",
  },
];

export default function LabAcquisitionsPage() {
  const [form, setForm] = useState({ labName: "", contactName: "", contactEmail: "", websiteUrl: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/lab-acquisition`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed");
      setSubmitted(true);
    } catch {
      setError("There was an issue submitting your inquiry. Please email us at info@labarium.com.");
    }
    setLoading(false);
  };

  const inputStyle = {
    width: "100%", padding: "13px 16px",
    border: `1.5px solid ${BRAND.lightGray}`,
    borderRadius: 10, fontFamily: "'Inter', sans-serif",
    fontSize: 14, color: BRAND.textDark,
    outline: "none", background: BRAND.white,
    boxSizing: "border-box", transition: "border-color 0.2s ease",
  };
  const labelStyle = {
    display: "block", fontFamily: "'Inter', sans-serif",
    fontSize: 11, fontWeight: 700, letterSpacing: "0.14em",
    color: BRAND.navy, marginBottom: 7, textTransform: "uppercase",
  };

  return (
    <>
      <PageHero
        eyebrow="ABOUT"
        title="Join Our"
        highlight="Lab Network"
        subtitle="Labarium is acquiring a nationwide network of clinical laboratories to provide a national platform for its services."
      />

      {/* Intro + Objectives */}
      <section className="rx-pad" style={{ background: BRAND.offWhite, padding: "80px 40px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>

          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
              <div style={{ width: 48, height: 1, background: BRAND.gold }} />
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.28em", color: BRAND.gold, textTransform: "uppercase" }}>Our Objectives</span>
              <div style={{ width: 48, height: 1, background: BRAND.gold }} />
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "clamp(26px,3.5vw,42px)", color: BRAND.navy, margin: "0 0 20px" }}>
              What We Look For in Partners
            </h2>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: BRAND.textMid, maxWidth: 640, margin: "0 auto", lineHeight: 1.75 }}>
              We're building a national platform for clinical laboratory services. The right partner shares our commitment to quality, innovation, and patient access.
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 24, marginBottom: 80 }}>
            {OBJECTIVES.map((obj, i) => (
              <div
                key={i}
                className="reveal"
                style={{
                  background: BRAND.white,
                  borderRadius: 16,
                  padding: "32px 28px",
                  border: `1px solid ${BRAND.lightGray}`,
                  boxShadow: "0 2px 16px rgba(0,30,80,0.05)",
                  transition: "all 0.3s ease",
                  position: "relative", overflow: "hidden",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 16px 48px rgba(0,53,122,0.12)"; e.currentTarget.style.borderColor = "rgba(201,169,110,0.3)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 16px rgba(0,30,80,0.05)"; e.currentTarget.style.borderColor = BRAND.lightGray; }}
              >
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${BRAND.gold}, rgba(201,169,110,0.2))` }} />
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: "rgba(0,53,122,0.07)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: BRAND.navy, marginBottom: 18,
                }}>
                  {obj.icon}
                </div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 20, color: BRAND.navy, margin: "0 0 10px" }}>{obj.title}</h3>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: BRAND.textMid, lineHeight: 1.75, margin: 0 }}>{obj.desc}</p>
              </div>
            ))}
          </div>

          {/* Submission form */}
          <div style={{ maxWidth: 680, margin: "0 auto" }}>
            <div style={{
              background: BRAND.white, borderRadius: 20,
              border: `1px solid ${BRAND.lightGray}`,
              overflow: "hidden",
              boxShadow: "0 8px 40px rgba(0,53,122,0.09)",
            }}>
              <div style={{ background: `linear-gradient(135deg, ${BRAND.navyDeep}, ${BRAND.navyDark})`, padding: "32px 40px", position: "relative" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldLight}, ${BRAND.gold})` }} />
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 28, color: BRAND.white, margin: "0 0 8px" }}>
                  Submit Your Lab
                </h2>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "rgba(255,255,255,0.55)", margin: 0 }}>
                  Tell us about your laboratory. Our team will be in touch within 2 business days.
                </p>
              </div>

              <div style={{ padding: "40px" }}>
                {submitted ? (
                  <div style={{ textAlign: "center", padding: "40px 20px" }}>
                    <div style={{
                      width: 72, height: 72, borderRadius: "50%",
                      background: "linear-gradient(135deg, rgba(201,169,110,0.15), rgba(201,169,110,0.05))",
                      border: "1.5px solid rgba(201,169,110,0.3)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      margin: "0 auto 24px",
                    }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke={BRAND.gold} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 24, color: BRAND.navy, marginBottom: 12 }}>Submission Received</h3>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: BRAND.textMid, lineHeight: 1.7 }}>
                      Thank you for your interest in joining the Labarium network. Our acquisitions team will review your submission and be in touch shortly.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: 20 }}>
                      <label style={labelStyle}>Name of Lab *</label>
                      <input name="labName" required value={form.labName} onChange={handleChange} placeholder="e.g. Sunrise Clinical Laboratory" style={inputStyle}
                        onFocus={e => e.target.style.borderColor = BRAND.navy}
                        onBlur={e => e.target.style.borderColor = BRAND.lightGray} />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label style={labelStyle}>Contact Name *</label>
                      <input name="contactName" required value={form.contactName} onChange={handleChange} placeholder="Full name" style={inputStyle}
                        onFocus={e => e.target.style.borderColor = BRAND.navy}
                        onBlur={e => e.target.style.borderColor = BRAND.lightGray} />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label style={labelStyle}>Contact Email *</label>
                      <input type="email" name="contactEmail" required value={form.contactEmail} onChange={handleChange} placeholder="you@yourlabname.com" style={inputStyle}
                        onFocus={e => e.target.style.borderColor = BRAND.navy}
                        onBlur={e => e.target.style.borderColor = BRAND.lightGray} />
                    </div>
                    <div style={{ marginBottom: 28 }}>
                      <label style={labelStyle}>Website URL</label>
                      <input type="url" name="websiteUrl" value={form.websiteUrl} onChange={handleChange} placeholder="https://yourlabname.com" style={inputStyle}
                        onFocus={e => e.target.style.borderColor = BRAND.navy}
                        onBlur={e => e.target.style.borderColor = BRAND.lightGray} />
                    </div>
                    {error && (
                      <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "12px 16px", fontFamily: "'Inter', sans-serif", fontSize: 13, color: "#b91c1c", marginBottom: 20 }}>{error}</div>
                    )}
                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: "100%",
                        background: loading ? BRAND.textMid : `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldLight})`,
                        color: BRAND.navyDeep,
                        border: "none",
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 700, fontSize: 13,
                        letterSpacing: "0.1em",
                        padding: "16px 24px",
                        borderRadius: 10,
                        cursor: loading ? "not-allowed" : "pointer",
                        textTransform: "uppercase",
                        boxShadow: loading ? "none" : "0 6px 24px rgba(201,169,110,0.35)",
                        transition: "all 0.25s ease",
                      }}
                    >
                      {loading ? "Submitting…" : "Submit Inquiry →"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
